import { formatPrice } from '@carbonify/ui/utils/format';
import { notEmpty } from 'utils/arrays';

import { Coupon, PriceMatrix, ProductOptionMatrix, ProductOptionWithPrice, YearOption } from './types';

const comboOptionPattern = /^(?<type>[A-Z]+)__(?<productOptionKey>[A-Z]+)___(?<ubaYears>(\d{4})(?:_\d{4})*)$/;

export function parseComboOption(productOption?: ProductOptionMatrix | string) {
  if (!productOption) return null;

  const key = typeof productOption === 'string' ? productOption : productOption.key;
  const match = key.match(comboOptionPattern);
  if (!match?.groups) return null;

  const { type, productOptionKey, ubaYears } = match.groups;

  return {
    type,
    key,
    productOptionKey,
    ubaYears: ubaYears.split('_').map(Number),
  };
}

export const getPriceFromProductOption = (productOption: ProductOptionMatrix, vehicleClass: string) => {
  if ('prices' in productOption && typeof productOption.prices?.[vehicleClass] === 'number') {
    return { value: productOption.prices?.[vehicleClass], relative: false };
  }

  if ('relativeAmounts' in productOption && typeof productOption.relativeAmounts?.[vehicleClass] === 'number') {
    return { value: productOption.relativeAmounts?.[vehicleClass], relative: true };
  }
};

const getProductPrice = (
  priceMatrix: PriceMatrix,
  productOption: ProductOptionMatrix,
  ubaYears: string[],
  vehicleClass: string,
) => {
  const hasMultipleUbaYears = ubaYears.length > 1;

  if (!hasMultipleUbaYears) {
    return getPriceFromProductOption(productOption, vehicleClass);
  }

  return ubaYears.reduce<{ value: number; relative: boolean } | undefined>(
    (result, year) => {
      if (!result) return undefined;

      const comboOption = priceMatrix[parseInt(year, 10)]?.productOptions?.find(({ key }) => key === productOption.key);
      const comboOptionPrice = comboOption && getPriceFromProductOption(comboOption, vehicleClass);
      if (!comboOptionPrice) return undefined;
      if (result.relative) return result;
      if (comboOptionPrice.relative) return comboOptionPrice;

      result.value += comboOptionPrice.value;
      return result;
    },
    { value: 0, relative: false },
  );
};

export const resolveProductOptionsForUbaYears = (
  priceMatrix: PriceMatrix,
  ubaYears: string[],
  vehicleClass: string,
): ProductOptionWithPrice[] => {
  if (!ubaYears.length) return [];

  const hasMultipleUbaYears = ubaYears.length > 1;
  const optionKeySuffix = `__${ubaYears.join('_')}`;
  const ubaYear = parseInt(ubaYears[0], 10);
  const config = priceMatrix[ubaYear];

  if (!config) return [];

  return (
    config.productOptions
      .filter(({ key }) => (hasMultipleUbaYears ? key.endsWith(optionKeySuffix) : !key.includes('COMBO')))
      .map((productOption): ProductOptionWithPrice | null => {
        const price = getProductPrice(priceMatrix, productOption, ubaYears, vehicleClass);
        const uspPoints = Object.values(productOption.uspPoints || {});

        const couponSorter = (a: Coupon, b: Coupon) => b.fixedAmount - a.fixedAmount;
        const priceBonusCoupon = productOption.priceBonusCoupons?.sort(couponSorter)[0];
        const productPriceCoupon = productOption.productPriceCoupons?.sort(couponSorter)[0];

        if (price) {
          return {
            ...productOption,
            coupon: priceBonusCoupon || (productPriceCoupon ? { ...productPriceCoupon, fixedAmount: 0 } : undefined),
            priceAsNode: price.relative
              ? `${price.value}%`
              : formatPrice(price.value + (productPriceCoupon?.fixedAmount || 0)),
            description: (
              <ol>
                {uspPoints.map((value, key) => (
                  <li key={key}>{value}</li>
                ))}
              </ol>
            ),
          };
        }

        return null;
      })
      .filter(notEmpty<ProductOptionWithPrice>) || []
  );
};

export const buildAvailableYearOptions = (
  priceMatrix: PriceMatrix,
  vehicleClass: string,
): { availableYears: YearOption[]; hasComboOptions: boolean } => {
  const candidates: YearOption[] = [];

  Object.entries(priceMatrix).forEach(([year, config]) => {
    config.productOptions.forEach((option) => {
      const comboOption = parseComboOption(option);
      if (comboOption && !candidates.some(({ value }) => value === comboOption.key)) {
        candidates.push({
          key: comboOption.ubaYears.join(','),
          value: comboOption.key,
          label: comboOption.ubaYears.join(', ').replace(/, ([^,]*)$/, ' & $1'),
        });
      }
    });

    if (config.productOptions.some(({ key }) => !key.includes('COMBO'))) {
      candidates.push({ key: year, value: year, label: year });
    }
  });

  const availableYears = candidates
    .filter((candidate) => {
      const ubaYears = candidate.key.split(',').filter(Boolean);
      return resolveProductOptionsForUbaYears(priceMatrix, ubaYears, vehicleClass).length > 0;
    })
    .sort((a, b) => a.value.localeCompare(b.value));

  if (!availableYears.length) {
    availableYears.push(...Object.keys(priceMatrix).map((year) => ({ key: year, value: year, label: year })));
  }

  return {
    availableYears,
    hasComboOptions: availableYears.some(({ key }) => key.includes(',')),
  };
};
